import { useState, useEffect, useCallback } from 'react';

/**
 * Convert hex color to RGB string
 */
const hexToRgb = (hex) => {
    if (!hex) return null;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null;
};

/**
 * MouseGlow Component
 * Creates a glow effect that follows the mouse cursor
 * Uses Cabaza brand colors by default, or custom colors from theme
 *
 * @param {number} opacity - Opacity of the glow (default: 0.24)
 * @param {number} size - Size of the glow in pixels (default: 350)
 * @param {number} blur - Blur amount in pixels (default: 100)
 * @param {string} primaryColor - Primary color hex (default: Cabaza purple #8B5CF6)
 * @param {string} secondaryColor - Secondary color hex (default: Cabaza pink #EC4899)
 */
const MouseGlow = ({
    opacity = 0.4,
    size = 15,
    blur = 0.1,
    primaryColor = '#8B5CF6',
    secondaryColor = '#EC4899'
}) => {
    const [position, setPosition] = useState({ x: -1000, y: -1000 });
    const [isVisible, setIsVisible] = useState(false);

    const handleMouseMove = useCallback((e) => {
        setPosition({ x: e.clientX, y: e.clientY });
        if (!isVisible) setIsVisible(true);
    }, [isVisible]);

    const handleMouseLeave = useCallback(() => {
        setIsVisible(false);
    }, []);

    const handleMouseEnter = useCallback(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        // Create a style element to hide cursor on all elements
        const style = document.createElement('style');
        style.id = 'mouse-glow-cursor-hide';
        style.textContent = '*, *::before, *::after { cursor: none !important; }';
        document.head.appendChild(style);

        window.addEventListener('mousemove', handleMouseMove);
        document.body.addEventListener('mouseleave', handleMouseLeave);
        document.body.addEventListener('mouseenter', handleMouseEnter);

        return () => {
            // Remove the style element on cleanup
            const existingStyle = document.getElementById('mouse-glow-cursor-hide');
            if (existingStyle) {
                existingStyle.remove();
            }
            window.removeEventListener('mousemove', handleMouseMove);
            document.body.removeEventListener('mouseleave', handleMouseLeave);
            document.body.removeEventListener('mouseenter', handleMouseEnter);
        };
    }, [handleMouseMove, handleMouseLeave, handleMouseEnter]);

    // Convert hex colors to RGB
    const primary = hexToRgb(primaryColor) || '139, 92, 246';
    const secondary = hexToRgb(secondaryColor) || '236, 72, 153';

    return (
        <div
            className="pointer-events-none fixed inset-0 z-[99999] overflow-hidden"
            aria-hidden="true"
        >
            {/* White outer glow for visibility on dark backgrounds */}
            <div
                className="absolute rounded-full transition-opacity duration-200"
                style={{
                    width: size + 1,
                    height: size + 1,
                    left: position.x - (size + 1) / 2,
                    top: position.y - (size + 1) / 2,
                    background: `rgba(255, 255, 255, 0.2)`,
                    filter: `blur(${blur}px)`,
                    opacity: isVisible ? 1 : 0,
                    transform: 'translate3d(0, 0, 0)',
                }}
            />
            {/* Main glow with gradient */}
            <div
                className="absolute rounded-full transition-opacity duration-200"
                style={{
                    width: size,
                    height: size,
                    left: position.x - size / 2,
                    top: position.y - size / 2,
                    background: `rgba(${primary}, ${opacity})`,
                    filter: `blur(${blur}px)`,
                    opacity: isVisible ? 1 : 0,
                    transform: 'translate3d(0, 0, 0)',
                }}
            />
        </div>
    );
};

export default MouseGlow;
